import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { SERVICES_DATA } from "../data";

export default function Header() {
  const { lang, t, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu and dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  const handleMouseEnterServices = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setServicesDropdownOpen(true);
  };

  const handleMouseLeaveServices = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setServicesDropdownOpen(false);
    }, 200);
  };

  const servicesList = SERVICES_DATA.map((s, idx) => ({
    id: s.id,
    num: `0${idx + 1}`,
    title: s.title[lang] || s.title.da,
    subtitle: s.subtitle?.[lang] || s.subtitle?.da,
    path: lang === "da" ? `/ydelser/${s.slug.da}` : `/en/services/${s.slug.en}`,
  }));

  const servicesOverviewPath = lang === "da" ? "/ydelser" : "/en/services";
  const contactPath = lang === "da" ? "/kontakt" : "/en/contact";
  const homePath = lang === "da" ? "/" : "/en";

  const isHomePage = location.pathname === "/" || location.pathname === "/en";
  const headerBgClass = isHomePage
    ? isScrolled
      ? "bg-stone-50/95 backdrop-blur-md border-b border-stone-200 py-4 shadow-sm"
      : "bg-transparent py-6"
    : "bg-stone-50/95 backdrop-blur-md border-b border-stone-200 py-4 shadow-sm";

  const textClass = isHomePage
    ? isScrolled
      ? "text-stone-900"
      : "text-white"
    : "text-stone-900";

  const textMutedClass = isHomePage
    ? isScrolled
      ? "text-stone-500"
      : "text-stone-300"
    : "text-stone-500";

  const navLinkClass = (isActive) => {
    if (isHomePage) {
      if (isScrolled) {
        return isActive ? "text-stone-900 font-semibold" : "text-stone-600 hover:text-stone-900";
      } else {
        return isActive ? "text-white font-semibold" : "text-stone-300 hover:text-white";
      }
    } else {
      return isActive ? "text-stone-900 font-semibold" : "text-stone-600 hover:text-stone-900";
    }
  };

  const navLinkLineClass = isHomePage
    ? isScrolled
      ? "bg-stone-900"
      : "bg-white"
    : "bg-stone-900";

  const isServicesActive =
    location.pathname === servicesOverviewPath ||
    location.pathname.startsWith("/ydelser/") ||
    location.pathname.startsWith("/en/services/") ||
    [
      "/totalentreprise",
      "/projektering-af-byggeri",
      "/toemrerarbejde",
      "/renovering",
      "/byggepladsservice",
      "/specialprojekter",
      "/en/turnkey",
      "/en/building-engineering",
      "/en/carpentry",
      "/en/renovation",
      "/en/construction-site-services",
      "/en/site-services",
      "/en/custom-projects",
    ].includes(location.pathname);

  const langSwitcherBgClass = isHomePage
    ? isScrolled
      ? "bg-stone-100 border border-stone-200"
      : "bg-stone-900/80 border border-stone-800"
    : "bg-stone-100 border border-stone-200";

  const langBtnClass = (isActive) => {
    if (isHomePage) {
      if (isScrolled) {
        return isActive ? "text-stone-900 bg-stone-200 font-semibold" : "text-stone-500 hover:text-stone-900";
      } else {
        return isActive ? "text-white bg-stone-800 font-semibold" : "text-stone-400 hover:text-stone-200";
      }
    } else {
      return isActive ? "text-stone-900 bg-stone-200 font-semibold" : "text-stone-500 hover:text-stone-900";
    }
  };

  const ctaClass = isHomePage
    ? isScrolled
      ? "text-stone-900 border border-stone-300 hover:bg-stone-900 hover:text-white hover:border-stone-900"
      : "text-white border border-stone-500 hover:bg-white hover:text-stone-950 hover:border-white"
    : "text-stone-900 border border-stone-300 hover:bg-stone-900 hover:text-white hover:border-stone-900";

  const mobileMenuBtnClass = isHomePage
    ? isScrolled
      ? "text-stone-900 hover:text-stone-600"
      : "text-white hover:text-stone-300"
    : "text-stone-900 hover:text-stone-600";

  // Dropdown appearance based on dark hero vs light background
  const dropdownCardClass = isHomePage && !isScrolled
    ? "bg-stone-900/98 border border-stone-800 text-stone-100 shadow-2xl backdrop-blur-xl"
    : "bg-stone-50/98 border border-stone-200 text-stone-900 shadow-2xl backdrop-blur-xl";

  const dropdownItemHoverClass = isHomePage && !isScrolled
    ? "hover:bg-stone-800/80 text-stone-200"
    : "hover:bg-stone-100 text-stone-800";

  const dropdownOverviewClass = isHomePage && !isScrolled
    ? "border-stone-800 text-stone-400 hover:text-white bg-stone-950/60"
    : "border-stone-200 text-stone-600 hover:text-stone-950 bg-stone-100/70";

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className={`w-full transition-all duration-500 ${headerBgClass}`}>
        <div className="w-full px-6 md:px-12 lg:px-16 flex items-center justify-between">
          {/* Logo */}
        <Link to={homePath} className="flex flex-col items-start group">
          <span className={`text-xl md:text-2xl font-light tracking-[0.25em] uppercase transition-colors duration-300 ${textClass}`}>
            CPH <span className="font-semibold">Collective</span>
          </span>
          <span className={`text-[9px] tracking-[0.4em] uppercase font-mono mt-0.5 ${textMutedClass}`}>
            {t("brand.subtagline")}
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-10">
          {/* Home */}
          <Link
            to={homePath}
            className={`text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 relative py-2 ${navLinkClass(
              location.pathname === homePath
            )}`}
          >
            {t("nav.home")}
            {location.pathname === homePath && (
              <span className={`absolute bottom-0 left-0 w-full h-[1px] transition-all duration-300 ${navLinkLineClass}`} />
            )}
          </Link>

          {/* Ydelser with Dropdown */}
          <div
            className="relative group py-2"
            onMouseEnter={handleMouseEnterServices}
            onMouseLeave={handleMouseLeaveServices}
          >
            <div className="flex items-center gap-1.5 cursor-pointer">
              <Link
                to={servicesOverviewPath}
                className={`text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 relative ${navLinkClass(
                  isServicesActive
                )}`}
              >
                {t("nav.services")}
                {isServicesActive && (
                  <span className={`absolute bottom-[-8px] left-0 w-full h-[1px] transition-all duration-300 ${navLinkLineClass}`} />
                )}
              </Link>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setServicesDropdownOpen((prev) => !prev);
                }}
                className={`p-0.5 transition-transform duration-300 group-hover:rotate-180 ${servicesDropdownOpen ? "rotate-180" : ""} ${navLinkClass(
                  isServicesActive
                )}`}
                aria-label="Fold ydelser ud"
                aria-expanded={servicesDropdownOpen}
              >
                <ChevronDown className="w-3.5 h-3.5 opacity-70" />
              </button>
            </div>

            {/* Dropdown Menu */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 w-80 lg:w-96 transition-all duration-300 origin-top z-50 group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto group-hover:translate-y-0 focus-within:opacity-100 focus-within:scale-100 focus-within:pointer-events-auto focus-within:translate-y-0 ${
                servicesDropdownOpen
                  ? "opacity-100 scale-100 pointer-events-auto translate-y-0"
                  : "opacity-0 scale-95 pointer-events-none -translate-y-2"
              }`}
            >
              <div className={`rounded-none p-2 divide-y divide-stone-200/20 ${dropdownCardClass}`}>
                {/* Services items list */}
                <div className="py-1">
                  {servicesList.map((service) => (
                    <Link
                      key={service.id}
                      to={service.path}
                      onClick={() => setServicesDropdownOpen(false)}
                      className={`group flex items-start gap-3 p-3 transition-colors duration-200 ${dropdownItemHoverClass}`}
                    >
                      <span className="font-mono text-[10px] text-stone-400 mt-0.5 group-hover:text-stone-900 transition-colors">
                        {service.num}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs uppercase tracking-widest font-medium flex items-center justify-between">
                          <span>{service.title}</span>
                          <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                        </div>
                        {service.subtitle && (
                          <p className="text-[11px] text-stone-500 line-clamp-1 mt-0.5 font-light">
                            {service.subtitle}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Direct Link to All Services Overview */}
                <div className="pt-2 pb-1 px-2">
                  <Link
                    to={servicesOverviewPath}
                    onClick={() => setServicesDropdownOpen(false)}
                    className={`flex items-center justify-between text-[11px] uppercase tracking-[0.2em] font-mono p-2.5 transition-colors duration-200 ${dropdownOverviewClass}`}
                  >
                    <span>{lang === "da" ? "Se alle ydelser (oversigt)" : "View all services (overview)"}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Projekter */}
          <Link
            to={lang === "da" ? "/projekter" : "/en/projects"}
            className={`text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 relative py-2 ${navLinkClass(
              location.pathname.startsWith("/projekter") || location.pathname.startsWith("/en/projects")
            )}`}
          >
            {t("nav.projects")}
            {(location.pathname.startsWith("/projekter") || location.pathname.startsWith("/en/projects")) && (
              <span className={`absolute bottom-0 left-0 w-full h-[1px] transition-all duration-300 ${navLinkLineClass}`} />
            )}
          </Link>

          {/* Om os */}
          <Link
            to={lang === "da" ? "/om-os" : "/en/about"}
            className={`text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 relative py-2 ${navLinkClass(
              location.pathname === "/om-os" || location.pathname === "/en/about"
            )}`}
          >
            {t("nav.about")}
            {(location.pathname === "/om-os" || location.pathname === "/en/about") && (
              <span className={`absolute bottom-0 left-0 w-full h-[1px] transition-all duration-300 ${navLinkLineClass}`} />
            )}
          </Link>

          {/* Kontakt */}
          <Link
            to={contactPath}
            className={`text-xs uppercase tracking-[0.3em] font-medium transition-all duration-300 relative py-2 ${navLinkClass(
              location.pathname === contactPath
            )}`}
          >
            {t("nav.contact")}
            {location.pathname === contactPath && (
              <span className={`absolute bottom-0 left-0 w-full h-[1px] transition-all duration-300 ${navLinkLineClass}`} />
            )}
          </Link>
        </nav>

        {/* Desktop Actions (Language Switcher + CTA) */}
        <div className="hidden md:flex items-center gap-6">
          {/* Language Selector */}
          <div className={`flex items-center gap-1 p-1 ${langSwitcherBgClass}`}>
            <button
              onClick={() => changeLanguage("da")}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono transition-all duration-300 cursor-pointer ${langBtnClass(
                lang === "da"
              )}`}
            >
              <span>🇩🇰</span>
              <span>DA</span>
            </button>
            <button
              onClick={() => changeLanguage("en")}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono transition-all duration-300 cursor-pointer ${langBtnClass(
                lang === "en"
              )}`}
            >
              <span>🇬🇧</span>
              <span>EN</span>
            </button>
          </div>

          {/* CTA */}
          <Link
            to={contactPath}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] px-5 py-2.5 transition-all duration-500 font-mono ${ctaClass}`}
          >
            {t("nav.getQuote")}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Mobile Menu Button & Language Selector */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Mobile Language Selector */}
          <div className={`flex items-center gap-1 p-0.5 ${langSwitcherBgClass}`}>
            <button
              onClick={() => changeLanguage("da")}
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono transition-all duration-300 cursor-pointer ${langBtnClass(
                lang === "da"
              )}`}
            >
              <span>🇩🇰</span>
              <span>DA</span>
            </button>
            <button
              onClick={() => changeLanguage("en")}
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono transition-all duration-300 cursor-pointer ${langBtnClass(
                lang === "en"
              )}`}
            >
              <span>🇬🇧</span>
              <span>EN</span>
            </button>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`transition-colors ${mobileMenuBtnClass}`}
            aria-label="Toggle Menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </div>

      {/* Mobile Navigation Overlay */}
      <div
        className={`fixed inset-x-0 top-[68px] bottom-0 bg-stone-50 z-50 flex flex-col justify-between p-6 md:p-8 transition-all duration-300 md:hidden overflow-y-auto shadow-2xl ${
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col gap-4 mt-4">
          {/* Mobile Home */}
          <Link
            to={homePath}
            className={`text-lg uppercase tracking-[0.2em] font-light py-2 border-b border-stone-200 ${
              location.pathname === homePath ? "text-stone-900 font-medium" : "text-stone-500"
            }`}
          >
            {t("nav.home")}
          </Link>

          {/* Mobile Services Accordion */}
          <div className="border-b border-stone-200 pb-2">
            <div className="flex items-center justify-between py-2">
              <Link
                to={servicesOverviewPath}
                className={`text-lg uppercase tracking-[0.2em] font-light ${
                  isServicesActive ? "text-stone-900 font-medium" : "text-stone-500"
                }`}
              >
                {t("nav.services")}
              </Link>
              <button
                type="button"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="p-1 text-stone-500 hover:text-stone-900 transition-colors"
                aria-label="Vis undersider for ydelser"
              >
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-300 ${
                    mobileServicesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {/* Sub-services list */}
            {mobileServicesOpen && (
              <div className="pl-3 pr-1 py-2 space-y-2.5 bg-stone-100/60 my-1 border-l-2 border-stone-800">
                <Link
                  to={servicesOverviewPath}
                  className="block text-xs uppercase tracking-wider font-mono font-medium text-stone-900 pb-1 border-b border-stone-200/80"
                >
                  → {lang === "da" ? "Se alle ydelser (oversigt)" : "View all services (overview)"}
                </Link>
                {servicesList.map((service) => (
                  <Link
                    key={service.id}
                    to={service.path}
                    className="flex items-center justify-between text-xs tracking-wider text-stone-600 hover:text-stone-950 py-1"
                  >
                    <span>
                      <span className="font-mono text-[10px] text-stone-400 mr-2">{service.num}</span>
                      {service.title}
                    </span>
                    <ArrowRight className="w-3 h-3 text-stone-400" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Projekter */}
          <Link
            to={lang === "da" ? "/projekter" : "/en/projects"}
            className={`text-lg uppercase tracking-[0.2em] font-light py-2 border-b border-stone-200 ${
              location.pathname.startsWith("/projekter") || location.pathname.startsWith("/en/projects")
                ? "text-stone-900 font-medium"
                : "text-stone-500"
            }`}
          >
            {t("nav.projects")}
          </Link>

          {/* Mobile Om os */}
          <Link
            to={lang === "da" ? "/om-os" : "/en/about"}
            className={`text-lg uppercase tracking-[0.2em] font-light py-2 border-b border-stone-200 ${
              location.pathname === "/om-os" || location.pathname === "/en/about"
                ? "text-stone-900 font-medium"
                : "text-stone-500"
            }`}
          >
            {t("nav.about")}
          </Link>

          {/* Mobile Kontakt */}
          <Link
            to={contactPath}
            className={`text-lg uppercase tracking-[0.2em] font-light py-2 border-b border-stone-200 ${
              location.pathname === contactPath ? "text-stone-900 font-medium" : "text-stone-500"
            }`}
          >
            {t("nav.contact")}
          </Link>
        </nav>

        <div className="flex flex-col gap-4 mt-8 mb-6">
          <Link
            to={contactPath}
            className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-stone-900 border border-stone-300 px-6 py-4 hover:bg-stone-900 hover:text-white transition-all duration-300 font-mono"
          >
            {t("cta.requestQuote")}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="text-stone-500 text-xs font-mono tracking-wider">
            CPH Collective · {t("brand.tagline")}
          </div>
        </div>
      </div>
    </header>
  );
}
